import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import { FilePlus2, UserPlus, Radar, UploadCloud, FileText, Briefcase, Sparkles, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, fmtUSD } from "@/lib/api";
import { useMe } from "@/lib/me";
import { canAccess, stageLabel, dealStageLabel, scopeLabel } from "@/lib/constants";
import { NAV } from "@/components/app/AppShell";

const Item = ({ onSelect, icon: Icon, children, hint, testid }) => (
  <CommandPrimitive.Item
    onSelect={onSelect}
    data-testid={testid}
    className="flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm aria-selected:bg-accent"
  >
    {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
    <span className="flex-1 truncate">{children}</span>
    {hint && <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{hint}</span>}
  </CommandPrimitive.Item>
);

const Group = ({ heading, children }) => (
  <CommandPrimitive.Group heading={heading} className="px-1.5 py-1 [&_[cmdk-group-heading]]:kicker [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5">
    {children}
  </CommandPrimitive.Group>
);

export default function CommandPalette({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ stories: [], deals: [], radar: [] });
  const navigate = useNavigate();
  const me = useMe();

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) return setRes({ stories: [], deals: [], radar: [] });
    const t = setTimeout(async () => {
      try {
        setRes((await api.get("/search", { params: { q } })).data);
      } catch {}
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };
  const ql = q.toLowerCase();
  const nav = NAV.filter((n) => (!n.area || canAccess(me.role, n.area)) && n.label.toLowerCase().includes(ql));
  const actions = [
    canAccess(me.role, "editStories") && { label: "Nuevo borrador de historia", icon: FilePlus2, to: "/editorial?new=1", id: "new-story" },
    canAccess(me.role, "crm") && { label: "Agregar lead comercial", icon: UserPlus, to: "/crm?new=1", id: "new-lead" },
    canAccess(me.role, "radar") && { label: "Ejecutar escaneo del Radar", icon: Radar, to: "/radar?scan=1", id: "scan" },
    canAccess(me.role, "webflow") && { label: "Enviar a Webflow", icon: UploadCloud, to: "/integraciones", id: "webflow" },
  ].filter(Boolean).filter((a) => a.label.toLowerCase().includes(ql));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl overflow-hidden p-0" data-testid="command-palette">
        <DialogTitle className="sr-only">Buscar en Mediana Hub</DialogTitle>
        <CommandPrimitive shouldFilter={false} className="bg-popover">
          <div className="flex items-center gap-2 border-b border-border px-3 pr-10">
            <Search className="h-4 w-4 text-muted-foreground" />
            <CommandPrimitive.Input
              value={q}
              onValueChange={setQ}
              placeholder="Busca historias, anunciantes, pitches o acciones…"
              className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              data-testid="cmdk-input"
            />
          </div>
          <CommandPrimitive.List className="max-h-[420px] overflow-y-auto py-1">
            <CommandPrimitive.Empty className="py-8 text-center text-sm text-muted-foreground">Sin resultados</CommandPrimitive.Empty>
            {actions.length > 0 && (
              <Group heading="Acciones rápidas">
                {actions.map((a) => (
                  <Item key={a.id} icon={a.icon} onSelect={() => go(a.to)} testid={`cmdk-action-${a.id}`}>
                    {a.label}
                  </Item>
                ))}
              </Group>
            )}
            {res.stories.length > 0 && (
              <Group heading="Historias">
                {res.stories.map((s) => (
                  <Item key={s.id} icon={FileText} hint={stageLabel(s.status)} onSelect={() => go(`/editorial/${s.id}`)} testid="cmdk-result-story">
                    {s.title}
                  </Item>
                ))}
              </Group>
            )}
            {res.deals.length > 0 && (
              <Group heading="Negocios">
                {res.deals.map((d) => (
                  <Item key={d.id} icon={Briefcase} hint={`${fmtUSD(d.dealValue)} · ${dealStageLabel(d.stage)}`} onSelect={() => go(`/crm?deal=${d.id}`)} testid="cmdk-result-deal">
                    {d.clientName}
                  </Item>
                ))}
              </Group>
            )}
            {res.radar.length > 0 && (
              <Group heading="Radar">
                {res.radar.map((r) => (
                  <Item key={r.id} icon={Sparkles} hint={scopeLabel(r.scope)} onSelect={() => go(`/radar?item=${r.id}`)} testid="cmdk-result-radar">
                    {r.suggestedTitle}
                  </Item>
                ))}
              </Group>
            )}
            {nav.length > 0 && (
              <Group heading="Ir a">
                {nav.map((n) => (
                  <Item key={n.to} icon={n.icon} hint={n.n} onSelect={() => go(n.to)} testid={`cmdk-nav-${n.id}`}>
                    {n.label}
                  </Item>
                ))}
              </Group>
            )}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}
